import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getHydrationManager } from '@/utils/hydrationManager';

/**
 * Hook that manages SEO updates during navigation
 * Always allows SEO updates, only performs cleanup of duplicates
 */
export function useNavigationSEO() {
  const location = useLocation();
  
  // Always allow SEO updates - no blocking logic
  const canUpdateSEO = true;

  useEffect(() => {
    // Clean up any duplicate meta tags on every route change
    cleanupDuplicateMetaTags();
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[useNavigationSEO] Route changed to ${location.pathname}, cleanup performed`);
    }
  }, [location.pathname]);

  return { canUpdateSEO, pathname: location.pathname };
}

/**
 * Removes duplicate meta tags to prevent conflicts
 */
function cleanupDuplicateMetaTags() {
  if (typeof window === 'undefined') return;

  // Remove ALL keywords tags (deprecated, Google ignores since 2009)
  const keywordsTags = document.querySelectorAll('meta[name="keywords"]');
  keywordsTags.forEach(tag => tag.remove());

  // Remove duplicate robots tags (keep only the last one)
  const robotsTags = document.querySelectorAll('meta[name="robots"]');
  if (robotsTags.length > 1) {
    for (let i = 0; i < robotsTags.length - 1; i++) {
      robotsTags[i].remove();
    }
  }

  // Remove duplicate description tags (keep only the last one)
  const descriptionTags = document.querySelectorAll('meta[name="description"]');
  if (descriptionTags.length > 1) {
    for (let i = 0; i < descriptionTags.length - 1; i++) {
      descriptionTags[i].remove();
    }
  }

  // Remove duplicate og:title tags (keep only the last one)
  const ogTitleTags = document.querySelectorAll('meta[property="og:title"]');
  if (ogTitleTags.length > 1) {
    for (let i = 0; i < ogTitleTags.length - 1; i++) {
      ogTitleTags[i].remove();
    }
  }

  // Remove duplicate canonical links (keep only the last one)
  const canonicalLinks = document.querySelectorAll('link[rel="canonical"]');
  if (canonicalLinks.length > 1) {
    for (let i = 0; i < canonicalLinks.length - 1; i++) {
      canonicalLinks[i].remove();
    }
  }
}
